import axios from "axios";
import axiosInstance from "../config/axiosConfig";
import config from "../config/api";
import { getFilesByLead, uploadFile } from "../store/slices/FileDataSlice";
import {
  createQuotation,
  fetchAllQuotations,
} from "../store/slices/generateQuotationSlice";
import { addLead } from "../store/slices/leadsSlice";
import { generateLeadId } from "../store/slices/lead/leadIdGenerationSlice";

export const COPIED_LEAD_STORAGE_KEY = "psi_copied_lead_v1";
export const COPIED_LEAD_CLIPBOARD_PREFIX = "PSI_LEAD_COPY:";

const normalizeTypeService = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          return (
            item.ServiceType ||
            item.serviceType ||
            item.servicetype ||
            item.name ||
            item.label ||
            ""
          );
        }
        return "";
      })
      .filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return value.includes(",")
      ? value.split(",").map((s) => s.trim()).filter(Boolean)
      : [value.trim()];
  }
  return [];
};

const resolveAssignedGroupName = (lead, groups = []) => {
  if (!lead) return "";

  const nested =
    lead?.groupid?.newGroupName ||
    lead?.groupid?.groupName ||
    lead?.groupid?.GroupName ||
    lead?.groupid?.name;
  if (nested) return nested;

  if (lead?.AssignedGroup) return lead.AssignedGroup;

  const gid =
    lead?.groupid?._id ||
    lead?.groupid?.id ||
    (typeof lead?.groupid === "string" ? lead.groupid : "");

  if (gid && Array.isArray(groups) && groups.length) {
    const matched = groups.find(
      (gr) => String(gr?._id || gr?.id) === String(gid)
    );
    if (matched) {
      return (
        matched.newGroupName ||
        matched.groupName ||
        matched.GroupName ||
        matched.name ||
        ""
      );
    }
  }

  return "";
};

const pickLeadFields = (lead, groups = []) => {
  if (!lead) return null;
  return {
    ClientName: lead.ClientName || "",
    ClientEmail: lead.ClientEmail || "",
    ClientPhone:
      lead.ClientPhone === null || lead.ClientPhone === undefined
        ? ""
        : String(lead.ClientPhone),
    ClientContactName: lead.ClientContactName || "",
    ProjectName: lead.ProjectName || "",
    LeadDate: lead.LeadDate || null,
    QuotedValue: lead.QuotedValue ?? "",
    Status: lead.Status || "",
    Source: lead.Source || "",
    TypeService: normalizeTypeService(lead.TypeService),
    AssignedGroup: resolveAssignedGroupName(lead, groups),
    groupid:
      lead?.groupid?._id ||
      lead?.groupid?.id ||
      (typeof lead?.groupid === "string" ? lead.groupid : "") ||
      "",
    LeadType: lead.LeadType || "",
    FollowupDate: lead.FollowupDate || null,
    Probability: lead.Probability || "",
    Reference: lead.Reference || "",
    comments: lead.comments || "",
    AssignedPeople: Array.isArray(lead.AssignedPeople)
      ? [...lead.AssignedPeople]
      : [],
  };
};

const normalizeFilesList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.payload)) return data.payload;
  if (Array.isArray(data?.files)) return data.files;
  return [];
};

const mapAttachmentMeta = (file) => ({
  id: file.id || file._id || null,
  filename: file.filename || file.name || file.fileName || "attachment",
  filepath: file.filepath || file.filePath || "",
  filepathurl:
    file.filepathurl ||
    file.fileUrl ||
    file.url ||
    file.filePath ||
    file.path ||
    "",
  selectedCategory:
    file.selectedCategory || file.category || "Miscellaneous",
});

const mimeFromFilename = (filename = "") => {
  const ext = String(filename).split(".").pop()?.toLowerCase() || "";
  const map = {
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };
  return map[ext] || "application/octet-stream";
};

const authHeaders = () => {
  const token = localStorage.getItem("token");
  if (!token || token === "undefined" || token === "null") return {};
  return { Authorization: `Bearer ${token}` };
};

const mapQuotationSnapshot = (q) => ({
  type: q.type || "GeneratedQuote",
  templateid: q.templateid || q.templateId || null,
  templatedata: q.templatedata || q.templateData || "",
  jsondata:
    typeof q.jsondata === "string"
      ? q.jsondata
      : typeof q.jsonData === "string"
        ? q.jsonData
        : JSON.stringify(q.jsondata || q.jsonData || {}),
});

const leadIdCandidates = (lead) => {
  const ids = [lead?._id, lead?.id].filter(Boolean).map(String);
  return [...new Set(ids)];
};

async function fetchAttachmentsForLead(dispatch, lead) {
  const ids = leadIdCandidates(lead);
  const collected = [];
  const seen = new Set();

  for (const id of ids) {
    try {
      const filesData = await dispatch(getFilesByLead(id)).unwrap();
      normalizeFilesList(filesData)
        .map(mapAttachmentMeta)
        .filter((a) => a.filepathurl)
        .forEach((a) => {
          const key = `${a.filename}::${a.filepathurl}`;
          if (!seen.has(key)) {
            seen.add(key);
            collected.push(a);
          }
        });
    } catch (err) {
      console.error("copyLead: failed to load attachments for", id, err);
    }
  }

  return collected;
}

async function fetchQuotationsForLead(dispatch, lead) {
  const ids = new Set(leadIdCandidates(lead));
  try {
    const allQuotations = await dispatch(fetchAllQuotations()).unwrap();
    const list = Array.isArray(allQuotations)
      ? allQuotations
      : Array.isArray(allQuotations?.payload)
        ? allQuotations.payload
        : Array.isArray(allQuotations?.data)
          ? allQuotations.data
          : [];

    return list
      .filter((q) => {
        const qLeadId = String(q?.leadid ?? q?.leadId ?? q?.leadID ?? "");
        if (!ids.has(qLeadId)) return false;
        const type = String(q?.type || "");
        return type !== "AutoSave";
      })
      .map(mapQuotationSnapshot)
      .filter((q) => q.templatedata || q.jsondata);
  } catch (err) {
    console.error("copyLead: failed to load quotations", err);
    return [];
  }
}

/** Build and persist a copy payload for a lead (fields + attachment URLs + quotation snapshots). */
export async function copyLeadToStorage(dispatch, lead, groups = []) {
  if (!lead) {
    throw new Error("Lead is missing");
  }
  const ids = leadIdCandidates(lead);
  if (!ids.length) {
    throw new Error("Lead id is missing");
  }

  const fields = pickLeadFields(lead, groups);
  const attachments = await fetchAttachmentsForLead(dispatch, lead);
  const quotations = await fetchQuotationsForLead(dispatch, lead);

  const payload = {
    version: 1,
    copiedAt: new Date().toISOString(),
    sourceLeadId: ids[0],
    sourceLeadIds: ids,
    fields,
    attachments,
    quotations,
  };

  try {
    sessionStorage.setItem(COPIED_LEAD_STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    // Quotations HTML can be large; keep fields + attachments if full payload fails
    console.warn("copyLead: full payload too large for sessionStorage", err);
    const slim = {
      ...payload,
      quotations: quotations.map((q) => ({
        type: q.type,
        templateid: q.templateid,
        templatedata: q.templatedata,
        jsondata: q.jsondata,
      })),
    };
    try {
      sessionStorage.setItem(COPIED_LEAD_STORAGE_KEY, JSON.stringify(slim));
    } catch (err2) {
      // Last resort: fields + attachment metadata only
      sessionStorage.setItem(
        COPIED_LEAD_STORAGE_KEY,
        JSON.stringify({
          ...payload,
          quotations: [],
          quotationCountDropped: quotations.length,
        })
      );
      console.warn("copyLead: stored without quotations due to size", err2);
    }
  }

  try {
    // Prefer a light clipboard marker; sessionStorage holds the full payload
    await navigator.clipboard.writeText(
      `${COPIED_LEAD_CLIPBOARD_PREFIX}${payload.sourceLeadId}`
    );
  } catch (_) {
    // sessionStorage is enough if clipboard permission is denied
  }

  return payload;
}

export function hasCopiedLead() {
  try {
    return !!sessionStorage.getItem(COPIED_LEAD_STORAGE_KEY);
  } catch {
    return false;
  }
}

export function peekCopiedLead() {
  try {
    const raw = sessionStorage.getItem(COPIED_LEAD_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearCopiedLead() {
  try {
    sessionStorage.removeItem(COPIED_LEAD_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function parseCopiedLeadFromClipboardText(text) {
  if (!text || typeof text !== "string") return null;
  const trimmed = text.trim();
  if (!trimmed.startsWith(COPIED_LEAD_CLIPBOARD_PREFIX)) return null;
  const body = trimmed.slice(COPIED_LEAD_CLIPBOARD_PREFIX.length);
  // Full JSON payload
  if (body.startsWith("{")) {
    try {
      return JSON.parse(body);
    } catch {
      return null;
    }
  }
  // Marker-only clipboard (lead id) → load from sessionStorage
  const fromStorage = peekCopiedLead();
  if (fromStorage) return fromStorage;
  return null;
}

async function fetchBlobFromUrl(url, filename = "") {
  if (!url) throw new Error("Missing file url");

  const isAbsolute = /^https?:\/\//i.test(url);
  const headers = authHeaders();
  const attempts = [];

  if (isAbsolute) {
    attempts.push(async () => {
      const res = await fetch(url, { mode: "cors", credentials: "include" });
      if (!res.ok) throw new Error(`fetch-cred ${res.status}`);
      return res.blob();
    });
    attempts.push(async () => {
      const res = await fetch(url, { mode: "cors", credentials: "omit" });
      if (!res.ok) throw new Error(`fetch ${res.status}`);
      return res.blob();
    });
    attempts.push(async () => {
      const res = await axios.get(url, {
        responseType: "blob",
        headers,
        withCredentials: true,
      });
      return res.data;
    });
    attempts.push(async () => {
      const res = await axios.get(url, { responseType: "blob", headers });
      return res.data;
    });
    attempts.push(async () => {
      const res = await axios.get(url, { responseType: "blob" });
      return res.data;
    });

    // Same-origin / backend-hosted file: strip origin and hit via axiosInstance
    try {
      const parsed = new URL(url);
      const backend = new URL(config.backendUrl, window.location.origin);
      if (parsed.origin === backend.origin || parsed.origin === window.location.origin) {
        const pathWithQuery = `${parsed.pathname}${parsed.search}`;
        const apiPrefix = backend.pathname.replace(/\/$/, "");
        const relative =
          apiPrefix && pathWithQuery.startsWith(apiPrefix)
            ? pathWithQuery.slice(apiPrefix.length) || "/"
            : pathWithQuery;
        attempts.push(async () => {
          const res = await axiosInstance.get(relative, {
            responseType: "blob",
          });
          return res.data;
        });
      }
    } catch (_) {
      // ignore URL parse errors
    }
  } else {
    attempts.push(async () => {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`fetch-rel ${res.status}`);
      return res.blob();
    });
    attempts.push(async () => {
      const res = await axiosInstance.get(url, { responseType: "blob" });
      return res.data;
    });
  }

  // Relative uploads path fallback (e.g. uploads/filesdata/x.pdf)
  if (filename || url) {
    const pathCandidate = url.replace(/^\/+/, "");
    if (!isAbsolute || /uploads\//i.test(url)) {
      const uploadPath = isAbsolute
        ? (() => {
            try {
              return new URL(url).pathname.replace(/^\/+/, "");
            } catch {
              return pathCandidate;
            }
          })()
        : pathCandidate;
      if (uploadPath) {
        attempts.push(async () => {
          const res = await axiosInstance.get(`/${uploadPath}`, {
            responseType: "blob",
          });
          return res.data;
        });
      }
    }
  }

  let lastErr = null;
  for (const attempt of attempts) {
    try {
      const blob = await attempt();
      if (blob && (blob.size > 0 || blob.type)) {
        const mime =
          blob.type && blob.type !== "application/octet-stream"
            ? blob.type
            : mimeFromFilename(filename || url);
        if (mime && mime !== blob.type) {
          return new Blob([blob], { type: mime });
        }
        return blob;
      }
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr || new Error("Failed to download file");
}

/** Download copied attachment URLs into File objects with selectedCategory (for Add Lead upload). */
export async function buildFilesFromCopiedAttachments(attachments = []) {
  const files = [];
  const backendOrigin = String(config.backendUrl || "").replace(/\/api\/?$/i, "");

  for (const meta of attachments) {
    if (!meta?.filepathurl && !meta?.filepath) continue;
    const fileName = meta.filename || "attachment";

    const urlCandidates = [];
    if (meta.filepathurl) urlCandidates.push(meta.filepathurl);
    if (meta.filepath) {
      const fp = String(meta.filepath).replace(/^\/+/, "");
      if (backendOrigin) {
        urlCandidates.push(`${backendOrigin}/${fp}`);
        if (!/^uploads\//i.test(fp)) {
          urlCandidates.push(`${backendOrigin}/uploads/${fp}`);
        }
      }
      urlCandidates.push(`/${fp}`);
      if (!/^uploads\//i.test(fp)) {
        urlCandidates.push(`/uploads/${fp}`);
      }
    }

    let blob = null;
    for (const url of [...new Set(urlCandidates.filter(Boolean))]) {
      try {
        blob = await fetchBlobFromUrl(url, fileName);
        if (blob && blob.size > 0) break;
        blob = null;
      } catch (err) {
        console.warn("copyLead: attachment URL failed", url, err?.message || err);
      }
    }

    if (!blob) {
      console.error("copyLead: failed to download attachment", meta.filename);
      continue;
    }

    try {
      const mime =
        (blob.type &&
        blob.type !== "application/octet-stream" &&
        !blob.type.includes("json") &&
        !blob.type.includes("html")
          ? blob.type
          : null) || mimeFromFilename(fileName);
      const file = new File([blob], fileName, { type: mime });
      file.selectedCategory = meta.selectedCategory || "Miscellaneous";
      files.push(file);
    } catch (err) {
      console.error("copyLead: failed to build file", meta.filename, err);
    }
  }
  return files;
}

/** Recreate GeneratedQuote quotations against a newly created lead id. */
export async function recreateCopiedQuotations(dispatch, newLeadId, quotations = []) {
  if (newLeadId == null || newLeadId === "" || !Array.isArray(quotations) || quotations.length === 0) {
    return { created: 0, failed: 0 };
  }

  // Backend looks up Leads by numeric `id` (not Mongo _id). Modal filters the same way.
  let resolvedLeadId = newLeadId;
  if (typeof newLeadId === "number" && Number.isFinite(newLeadId)) {
    resolvedLeadId = newLeadId;
  } else if (/^\d+$/.test(String(newLeadId).trim())) {
    resolvedLeadId = Number(String(newLeadId).trim());
  } else {
    // Mongo ObjectId was passed — quotations would 404 ("Lead not found")
    console.error(
      "copyLead: quotation leadid must be numeric lead.id, got:",
      newLeadId
    );
    return { created: 0, failed: quotations.length };
  }

  let created = 0;
  let failed = 0;

  for (const q of quotations) {
    if (String(q.type || "") === "AutoSave") continue;
    if (!q?.templatedata && !q?.jsondata) continue;
    if (!q?.templateid) {
      failed += 1;
      console.error("copyLead: quotation missing templateid, skip");
      continue;
    }
    try {
      await dispatch(
        createQuotation({
          type: q.type || "GeneratedQuote",
          leadid: resolvedLeadId,
          templateid: q.templateid,
          templatedata: q.templatedata || "",
          jsondata: q.jsondata || "{}",
        })
      ).unwrap();
      created += 1;
    } catch (err) {
      failed += 1;
      console.error("copyLead: failed to recreate quotation", err);
    }
  }

  return { created, failed };
}

export function toDateOrNull(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function withCopyProjectName(projectName = "") {
  const base = String(projectName || "").trim();
  if (!base) return "";
  if (/\(Copy\)$/i.test(base)) return base;
  return `${base} (Copy)`;
}

/**
 * Create a new lead from a copied payload (Lead Report paste).
 * Forces Status=Pending and SalesPersonStatus=project requested.
 * Copies attachments + quotations onto the new lead.
 */
export async function pasteLeadAsNewLead(
  dispatch,
  {
    payload,
    leadOwner = "",
    leadOwnerEmail = "",
    groups = [],
  } = {}
) {
  if (!payload?.fields) {
    throw new Error("No copied lead data found");
  }

  const f = payload.fields;
  const groupsList = Array.isArray(groups) ? groups : [];
  const groupName = f.AssignedGroup || "";
  const groupId = f.groupid || "";
  const matchedGroup =
    groupsList.find((g) => g?.newGroupName === groupName) ||
    groupsList.find((g) => String(g?._id || g?.id) === String(groupId)) ||
    null;

  const resolvedGroupName =
    matchedGroup?.newGroupName || groupName || "";
  const resolvedGroupId =
    matchedGroup?._id || matchedGroup?.id || groupId || undefined;

  const projectName = withCopyProjectName(f.ProjectName || "");
  const clientName = f.ClientName || "";
  const typeService = Array.isArray(f.TypeService)
    ? f.TypeService.filter(Boolean)
    : normalizeTypeService(f.TypeService);

  const firstLetterGroup = resolvedGroupName
    ? resolvedGroupName.trim().charAt(0).toUpperCase()
    : "";
  const firstLetterClient = clientName
    ? clientName.trim().charAt(0).toUpperCase()
    : "";
  const firstLetterProject = projectName
    ? projectName.trim().charAt(0).toUpperCase()
    : "";

  let quotationId = "";
  if (firstLetterGroup && firstLetterClient && firstLetterProject) {
    try {
      const idRes = await dispatch(
        generateLeadId({
          group: firstLetterGroup,
          clientName: firstLetterClient,
          projectName: firstLetterProject,
        })
      ).unwrap();
      quotationId = idRes?.quotationId || "";
    } catch (err) {
      console.error("pasteLead: failed to generate quotationId", err);
    }
  }

  const newLead = {
    ClientName: clientName,
    ClientEmail: f.ClientEmail || "",
    ProjectName: projectName,
    LeadDate: toDateOrNull(f.LeadDate) || new Date(),
    ClientPhone:
      f.ClientPhone === null || f.ClientPhone === undefined
        ? ""
        : String(f.ClientPhone),
    QuotedValue: f.QuotedValue ?? "",
    ClientContactName: f.ClientContactName || "",
    Status: "Pending",
    Source: f.Source || "",
    TypeService: typeService,
    AssignedGroup: resolvedGroupName,
    groupid: resolvedGroupId,
    LeadType: f.LeadType || "",
    FollowupDate: toDateOrNull(f.FollowupDate),
    Probability: f.Probability || "",
    Reference: f.Reference || "",
    comments: f.comments || "",
    AssignedPeople: Array.isArray(f.AssignedPeople) ? [...f.AssignedPeople] : [],
    LeadOwner: leadOwner,
    LeadOwnerEmail: leadOwnerEmail,
    quotationId,
    SalesPersonStatus: "project requested",
  };

  const response = await dispatch(addLead(newLead)).unwrap();
  const created = response?.lead || response;
  // Files are keyed by Mongo _id; quotations must use numeric lead.id
  const fileLeadId = created?._id ? String(created._id) : null;
  const quotationLeadId =
    created?.id != null && created?.id !== "" && Number.isFinite(Number(created.id))
      ? Number(created.id)
      : null;
  if (!fileLeadId) {
    throw new Error("Lead created but id is missing");
  }
  if (quotationLeadId == null) {
    console.warn(
      "pasteLead: numeric lead.id missing on create response; quotations may not attach"
    );
  }

  // Re-fetch from source at paste time so large quotations / attachments are not lost from storage
  const sourceIds = Array.isArray(payload.sourceLeadIds)
    ? payload.sourceLeadIds.filter(Boolean)
    : [payload.sourceLeadId].filter(Boolean);
  const sourceLead = {
    _id:
      sourceIds.find((id) => /^[a-f\d]{24}$/i.test(String(id))) ||
      sourceIds[0],
    id:
      sourceIds.find((id) => !/^[a-f\d]{24}$/i.test(String(id))) ||
      sourceIds[1] ||
      sourceIds[0],
  };

  let attachmentMetas = Array.isArray(payload.attachments)
    ? [...payload.attachments]
    : [];
  let quotations = Array.isArray(payload.quotations)
    ? [...payload.quotations]
    : [];

  if (sourceLead._id || sourceLead.id) {
    try {
      const liveAttachments = await fetchAttachmentsForLead(dispatch, sourceLead);
      if (liveAttachments.length > 0) {
        attachmentMetas = liveAttachments;
      }
    } catch (err) {
      console.error("pasteLead: live attachment fetch failed", err);
    }
    try {
      const liveQuotations = await fetchQuotationsForLead(dispatch, sourceLead);
      if (liveQuotations.length > 0) {
        quotations = liveQuotations;
      }
    } catch (err) {
      console.error("pasteLead: live quotation fetch failed", err);
    }
  }

  let attachmentsUploaded = 0;
  if (attachmentMetas.length > 0) {
    const files = await buildFilesFromCopiedAttachments(attachmentMetas);
    if (files.length > 0) {
      const uploadDate = new Date().toISOString().slice(0, 10);
      // Upload one-by-one (same pattern as Lead Report attachments modal)
      for (const file of files) {
        try {
          const formData = new FormData();
          formData.append("leadId", fileLeadId);
          formData.append("uploadDate", uploadDate);
          formData.append(
            "selectedCategory",
            file.selectedCategory || "Miscellaneous"
          );
          formData.append("files", file);
          await dispatch(uploadFile(formData)).unwrap();
          attachmentsUploaded += 1;
        } catch (oneErr) {
          console.error(
            "pasteLead: failed to upload attachment",
            file.name,
            oneErr
          );
        }
      }
    } else if (attachmentMetas.length > 0) {
      console.error(
        "pasteLead: could not download any of",
        attachmentMetas.length,
        "attachment(s) for re-upload"
      );
    }
  }

  const quoteResult =
    quotationLeadId != null
      ? await recreateCopiedQuotations(dispatch, quotationLeadId, quotations)
      : { created: 0, failed: quotations.length };

  // Refresh quotation list so Lead Report attachments modal sees the new quotes
  try {
    await dispatch(fetchAllQuotations());
  } catch (_) {
    // non-blocking
  }

  clearCopiedLead();

  return {
    lead: created,
    sourceLeadId: payload.sourceLeadId || payload.sourceLeadIds?.[0] || null,
    attachmentsUploaded,
    attachmentsExpected: attachmentMetas.length,
    quotationsCreated: quoteResult.created,
    quotationsFailed: quoteResult.failed,
  };
}

export { normalizeTypeService, resolveAssignedGroupName };
